import { useCallback } from "react";

import { ApolloError, FetchResult, useMutation } from "@apollo/client";

import {
  ADD_WORKSPACE_DASHBOARD,
  DASHBOARDS,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import { Dashboard, Workspace } from "models/workspace";

interface UseAddDashboardOptions {
  onSuccess?: (dashboardId?: string) => void;
  onError?: (error: string) => void;
}

export type UseAddDashboardToWorkspaceReturnT = {
  mutate: (workspace: Workspace, dashboard: Dashboard) => Promise<FetchResult>;
  isLoading: boolean;
};

export function useAddDashboardToWorkspace({
  onSuccess,
  onError
}: UseAddDashboardOptions = {}): UseAddDashboardToWorkspaceReturnT {
  const [addWorkspaceDashboard, { loading }] = useMutation(ADD_WORKSPACE_DASHBOARD);

  const mutate = useCallback(
    async (workspace: Workspace, dashboard: Dashboard) => {
      if (!workspace) return;

      return addWorkspaceDashboard({
        variables: {
          workspaceId: workspace.workspaceId,
          dashboardId: dashboard.dashboardId,
          position: workspace.dashboards.length
        },
        context: {
          clientName: WorkspaceClientName
        },
        refetchQueries: [
          { query: WORKSPACES, context: { clientName: WorkspaceClientName } },
          {
            query: WORKSPACE,
            variables: { id: workspace.workspaceId },
            context: { clientName: WorkspaceClientName }
          },
          { query: DASHBOARDS, context: { clientName: WorkspaceClientName } }
        ],
        onCompleted: () => {
          onSuccess?.(dashboard.dashboardId);
        },
        onError: (error: ApolloError) => {
          // eslint-disable-next-line no-console
          console.error(error.message, error);
          onError?.(error.message);
        }
      });
    },
    [addWorkspaceDashboard, onSuccess, onError]
  );

  return { mutate: mutate, isLoading: loading };
}
